import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import usePrevious from 'use-previous';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, injectIntl } from 'react-intl';
import { allowFreeText } from '@bibliocommons/utils-user';
import { catalogBibShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';
import { UserRatingDropdownContainer } from 'app/components/shared/UserRatingDropdown';
import Panel from 'app/components/shared/Panel';
import TextUgcEditor from '../TextUgcEditor';

import './CommentEditor.scss';

const EDITOR_KEY = 'comment';

export function CommentEditor({
  catalogBib,
  commentId,
  handleSuccess,
  handleCancel,
  open,
  initialContent,
  currentUser,
  commentsTransactions,
  commentsActions,
  ugcEditorActions,
  intl
}) {
  const prevOpen = usePrevious(open);
  const addButtonRef = useRef();
  const editorRef = useRef();

  useEffect(() => {
    // Set focus on the form when it opens
    if (open && !prevOpen && editorRef.current) {
      setFocusWithinElement(editorRef.current);
    }

    // Set focus on the add button when the form is closed
    if (!open && prevOpen && addButtonRef.current) {
      setFocusWithinElement(addButtonRef.current);
    }
  }, [open, prevOpen, editorRef, addButtonRef]);

  function handleRequestSuccess() {
    if (!commentId) ugcEditorActions.closeEditor(EDITOR_KEY);
    if (handleSuccess) handleSuccess();
  }

  function handleEditorClose() {
    if (!commentId) ugcEditorActions.closeEditor(EDITOR_KEY);
    if (handleCancel) handleCancel();
  }

  function addComment(commentContent) {
    commentsActions.addComment({
      metadataId: catalogBib.get('metadataId'),
      comment: commentContent,
      postSuccess: handleRequestSuccess
    });
  }

  function updateComment(commentContent) {
    commentsActions.updateComment({
      metadataId: catalogBib.get('metadataId'),
      commentId,
      comment: commentContent,

      postSuccess: handleRequestSuccess
    });
  }

  function renderCollapsed() {
    return (
      <Panel>
        <div className="comment-editor__label">
          <FormattedMessage id="add_comment_prompt" />
        </div>

        <button
          ref={addButtonRef}
          id="add-comment"
          className="comment-editor__add-comment"
          onClick={() => ugcEditorActions.openEditor(EDITOR_KEY)}
          type="button"
          data-key="comment-editor"
        >
          <span aria-hidden="true">
            <FormattedMessage id="add_comment" />
          </span>
          <ScreenReaderMessage>
            <FormattedMessage id="add_comment_sr_only" />
          </ScreenReaderMessage>
        </button>
      </Panel>
    );
  }

  function renderExpanded() {
    const metadataId = catalogBib.get('metadataId');
    const isLoading =
      commentsTransactions.getIn([metadataId, 'isAdding']) ||
      commentsTransactions.getIn([metadataId, commentId, 'isUpdating']);
    const label = commentId
      ? intl.formatMessage({ id: 'edit_comment_prompt' })
      : intl.formatMessage({ id: 'add_comment_prompt' });

    return (
      <TextUgcEditor
        ref={editorRef}
        ugcType="comment"
        metadataId={metadataId}
        handleConfirm={commentId ? updateComment : addComment}
        handleCancel={handleEditorClose}
        label={label}
        isLoading={isLoading}
        initialContent={initialContent}
      >
        <UserRatingDropdownContainer metadataId={metadataId} size="large" useLibraryBranding={false} />
      </TextUgcEditor>
    );
  }

  if (!currentUser || allowFreeText(currentUser)) {
    return <div className="cp-comment-editor">{open ? renderExpanded() : renderCollapsed()}</div>;
  }

  return null;
}

CommentEditor.defaultProps = {
  initialContent: ''
};

CommentEditor.propTypes = {
  catalogBib: catalogBibShape,
  currentUser: userIdentityShape,
  commentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  handleSuccess: PropTypes.func,
  handleCancel: PropTypes.func,
  open: PropTypes.bool,
  initialContent: PropTypes.string,
  commentsTransactions: ImmutablePropTypes.map.isRequired,
  commentsActions: PropTypes.objectOf(PropTypes.func),
  ugcEditorActions: PropTypes.objectOf(PropTypes.func)
};

export default injectIntl(CommentEditor);
